import AxiosConfig from '../utils/AxiosConfig';

class CoreBillingService {
  async getCompanies(search = '') {
    const params = new URLSearchParams();
    if (search && search.trim()) {
      params.append('search', search.trim());
    }
    const query = params.toString();
    const url = query ? `/core-billing/companies?${query}` : '/core-billing/companies';
    const response = await AxiosConfig.get(url);
    return response.data.data || [];
  }

  async getMonthlyOverview(year, month) {
    const response = await AxiosConfig.get(`/core-billing/reconcile/monthly-overview?year=${year}&month=${month}`);
    return response.data.data || [];
  }

  async getMyPaymentStatus() {
    const response = await AxiosConfig.get('/core-billing/my-payment-status');
    return response.data.data;
  }

  async getCompanyTransactions(companyId) {
    const response = await AxiosConfig.get(`/core-billing/companies/${companyId}/transactions`);
    return response.data.data || [];
  }

  async getCompanyDetail(companyId) {
    const response = await AxiosConfig.get(`/core-billing/companies/${companyId}/detail`);
    return response.data.data;
  }

  async getBillingAccountDetail(billingAccountId) {
    const response = await AxiosConfig.get(`/core-billing/billing-accounts/${billingAccountId}/detail`);
    return response.data.data;
  }

  async updateBillingAccount(billingAccountId, payload) {
    const response = await AxiosConfig.put(`/core-billing/billing-accounts/${billingAccountId}`, payload);
    return response.data.data;
  }

  async getBillingAccountTransactions(billingAccountId) {
    const response = await AxiosConfig.get(`/core-billing/billing-accounts/${billingAccountId}/transactions`);
    return response.data.data || [];
  }

  async grantBillingBenefit(billingAccountId, payload) {
    const response = await AxiosConfig.post(`/core-billing/billing-accounts/${billingAccountId}/benefits`, payload);
    return response.data.data;
  }

  async sendSubscriptionLink(companyId) {
    const response = await AxiosConfig.post(`/core-billing/companies/${companyId}/subscription/send-link`, {});
    return response.data.data;
  }

  async sendSubscriptionLinkByBillingAccount(billingAccountId) {
    const response = await AxiosConfig.post(`/core-billing/billing-accounts/${billingAccountId}/subscription/send-link`, {});
    return response.data.data;
  }

  async createSubscriptionLink(companyId) {
    const response = await AxiosConfig.post(`/core-billing/companies/${companyId}/subscription/create-link`, {});
    return response.data.data;
  }

  async createSubscriptionLinkByBillingAccount(billingAccountId) {
    const response = await AxiosConfig.post(`/core-billing/billing-accounts/${billingAccountId}/subscription/create-link`, {});
    return response.data.data;
  }

  async reconcileMercadoPagoStatuses(billingAccountId = null) {
    const path = billingAccountId ? `/core-billing/reconcile/${billingAccountId}` : '/core-billing/reconcile';
    const response = await AxiosConfig.post(path, {});
    return response.data.data;
  }

  async linkPreapproval(billingAccountId, preapprovalId) {
    const response = await AxiosConfig.post(
      `/core-billing/billing-accounts/${billingAccountId}/subscription/link-preapproval`,
      { preapprovalId }
    );
    return response.data.data;
  }

  async lookupMercadoPagoPaymentPreapproval(paymentId) {
    const response = await AxiosConfig.get(
      `/core-billing/mercadopago/payments/${encodeURIComponent(paymentId)}/preapproval`
    );
    return response.data.data;
  }

  async pauseSubscription(companyId) {
    const response = await AxiosConfig.post(`/core-billing/companies/${companyId}/subscription/pause`, {});
    return response.data.data;
  }

  async pauseSubscriptionByBillingAccount(billingAccountId) {
    const response = await AxiosConfig.post(`/core-billing/billing-accounts/${billingAccountId}/subscription/pause`, {});
    return response.data.data;
  }

  async reactivateSubscription(companyId) {
    const response = await AxiosConfig.post(`/core-billing/companies/${companyId}/subscription/reactivate`, {});
    return response.data.data;
  }

  async reactivateSubscriptionByBillingAccount(billingAccountId) {
    const response = await AxiosConfig.post(`/core-billing/billing-accounts/${billingAccountId}/subscription/reactivate`, {});
    return response.data.data;
  }

  async cancelSubscription(companyId) {
    const response = await AxiosConfig.post(`/core-billing/companies/${companyId}/subscription/cancel`, {});
    return response.data.data;
  }

  async cancelSubscriptionByBillingAccount(billingAccountId) {
    const response = await AxiosConfig.post(`/core-billing/billing-accounts/${billingAccountId}/subscription/cancel`, {});
    return response.data.data;
  }

  async backfillInvoices(companyId, fromDate = null) {
    const response = await AxiosConfig.post(`/core-billing/companies/${companyId}/invoices/backfill`, { fromDate });
    return response.data.data;
  }

  async backfillInvoicesByBillingAccount(billingAccountId, fromDate = null) {
    const response = await AxiosConfig.post(`/core-billing/billing-accounts/${billingAccountId}/invoices/backfill`, { fromDate });
    return response.data.data;
  }

  async markInvoicePaid(companyId, invoiceId, notes = '') {
    const response = await AxiosConfig.post(
      `/core-billing/companies/${companyId}/invoices/${invoiceId}/mark-paid`,
      { notes }
    );
    return response.data.data;
  }

  async syncInvoicePaidStatus(companyId, invoiceId, notes = '') {
    const response = await AxiosConfig.post(
      `/core-billing/companies/${companyId}/invoices/${invoiceId}/sync-paid-status`,
      { notes }
    );
    return response.data.data;
  }

  async updateInvoiceStatus(billingAccountId, invoiceId, payload) {
    const response = await AxiosConfig.post(
      `/core-billing/billing-accounts/${billingAccountId}/invoices/${invoiceId}/status`,
      payload
    );
    return response.data.data;
  }

  async generatePendingPaymentLink(companyId, invoiceId = null) {
    const response = await AxiosConfig.post(
      `/core-billing/companies/${companyId}/pending-payment-link`,
      { invoiceId }
    );
    return response.data.data;
  }

  async generatePendingPaymentLinkByBillingAccount(billingAccountId, invoiceId = null) {
    const response = await AxiosConfig.post(
      `/core-billing/billing-accounts/${billingAccountId}/pending-payment-link`,
      { invoiceId }
    );
    return response.data.data;
  }

  async getInvoicePdf(companyId, invoiceId) {
    const response = await AxiosConfig.get(
      `/core-billing/companies/${companyId}/invoices/${invoiceId}/pdf`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  async generateExtrasPaymentLink(companyId) {
    const response = await AxiosConfig.post(`/core-billing/companies/${companyId}/extras/payment-link`, {});
    return response.data.data;
  }

  async generateExtrasPaymentLinkByBillingAccount(billingAccountId) {
    const response = await AxiosConfig.post(`/core-billing/billing-accounts/${billingAccountId}/extras/payment-link`, {});
    return response.data.data;
  }
}

export default new CoreBillingService();
